// src/pages/Dashboard/Admin/AdherentPret/AdherentPretDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";
import { getUserRoleId } from "../../../config/dashboardMenuConfig";

import { useAdherentPretDashboard } from "./hooks/useAdherentPretDashboard";

import DashboardPretHeader from "./components/DashboardPretHeader";
import PretFiltersCard from "./components/PretFiltersCard";
import PretKpiCards from "./components/PretKpiCards";
import PretDataTable from "./components/PretDataTable";
import PretMobileCards from "./components/PretMobileCards";
import PretEmptyState from "./components/PretEmptyState";
import AddPretDrawer from "./components/AddPretDrawer";
import EditPretDrawer from "./components/EditPretDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function UnauthorizedDashboardPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Prêt de matériel
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs du club.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AdherentPretDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getUserRoleId(user || storedUser);

  const {
    prets,
    filteredPrets,
    adherents,
    articlesPret,
    stats,
    filters,
    setFilters,
    loading,
    loadingMeta,
    saving,
    deletingId,
    addDrawerOpen,
    editDrawerOpen,
    currentPret,
    openAddDrawer,
    closeAddDrawer,
    openEditDrawer,
    closeEditDrawer,
    handleCreatePret,
    handleUpdatePret,
    handleDeletePret,
    refreshPrets,
  } = useAdherentPretDashboard();

  if (roleId !== 1) {
    return <UnauthorizedDashboardPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <DashboardPretHeader />

        <PretFiltersCard
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          onAdd={openAddDrawer}
          onRefresh={refreshPrets}
        />

        <PretKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste des prêts
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Matériel prêté aux adhérents
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Suivez les prêts en cours, les retours effectués et les prêts
                  anciens à relancer.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Prêts affichés :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredPrets.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {prets.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredPrets.length === 0 ? (
              <PretEmptyState onAdd={openAddDrawer} />
            ) : (
              <div className="mt-5">
                <PretDataTable
                  prets={filteredPrets}
                  onEdit={openEditDrawer}
                  onDelete={handleDeletePret}
                  deletingId={deletingId}
                />

                <PretMobileCards
                  prets={filteredPrets}
                  onEdit={openEditDrawer}
                  onDelete={handleDeletePret}
                  deletingId={deletingId}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <AddPretDrawer
        isOpen={addDrawerOpen}
        onClose={closeAddDrawer}
        onSubmit={handleCreatePret}
        adherents={adherents}
        articlesPret={articlesPret}
        loadingMeta={loadingMeta}
        saving={saving}
      />

      <EditPretDrawer
        isOpen={editDrawerOpen}
        onClose={closeEditDrawer}
        pret={currentPret}
        onSubmit={handleUpdatePret}
        onDelete={handleDeletePret}
        saving={saving}
        deletingId={deletingId}
      />

      <ToastContainer />
    </section>
  );
}
